/**
 * items.ts — ground-truth ruleset + basket scoring.
 *
 * Ported from `_proto_kitchen_v1.py` `score_basket` / `best_possible_basket`
 * / `subset_accuracy`. This is the ground truth the agent must LEARN, not a
 * model. The env uses it to compute per-step reward; the harness uses it to
 * score the agent's basket.
 *
 * The score is NON-ADDITIVE: it sums per-item values (context-dependent)
 * plus forbidden-pair penalties plus recipe bonuses. The pair/recipe terms
 * are pure conjunctions — they have no linear decomposition, which is the
 * load-bearing fact the linear foil demonstrates.
 */
import {
  K, ITEM_VALUE, FORBIDDEN_PAIRS, RECIPES, N_CTX, BASKET_CAP, R_DUPLICATE,
  PICKUP, SKIP,
} from './constants';

/** Ground-truth reward of a basket (bitmask of K items) at context `ctx`. */
export function scoreBasket(basketMask: number, ctx: number): number {
  let total = 0;
  const items: number[] = [];
  for (let i = 0; i < K; i++) {
    if (basketMask & (1 << i)) {
      items.push(i);
      total += ITEM_VALUE[i][ctx];
    }
  }
  const itemSet = new Set(items);
  for (const [i, j, pen] of FORBIDDEN_PAIRS) {
    if (itemSet.has(i) && itemSet.has(j)) total += pen;
  }
  for (const r of RECIPES) {
    let ok = true;
    for (const it of r.items) if (!itemSet.has(it)) { ok = false; break; }
    if (ok) total += r.bonus;
  }
  return total;
}

/** Reward for a PICKUP decision on `item` given the current basket+ctx.
 *  This is the canonical pickup reward used everywhere (env.step, harness
 *  counterfactuals, live tick). A pickup of an item type ALREADY in the
 *  basket is a no-op on scoreBasket (the basket is a set), so without a
 *  penalty the agent would see reward 0 for both pickup and skip on a
 *  duplicate and have no signal to learn "skip already-collected types".
 *  We apply R_DUPLICATE to give that signal — the tree learns the
 *  basket[item]=1 ∧ action=pickup → -0.5 conjunction; the linear foil
 *  provably cannot isolate it. */
export function pickupReward(basketMask: number, item: number, ctx: number): number {
  if (basketMask & (1 << item)) {
    // Duplicate pickup — type already collected.
    return R_DUPLICATE;
  }
  const before = scoreBasket(basketMask, ctx);
  const after = scoreBasket(basketMask | (1 << item), ctx);
  return after - before;
}

/** Brute-force the best basket at this context (cap = BASKET_CAP). K=8 →
 *  256 subsets, trivial. Returns [mask, score]. */
export function bestPossibleBasket(ctx: number, cap = BASKET_CAP): readonly [number, number] {
  let bestMask = 0;
  let bestScore = -Infinity;
  for (let mask = 0; mask < (1 << K); mask++) {
    let n = 0;
    for (let i = 0; i < K; i++) if (mask & (1 << i)) n++;
    if (n > cap) continue;
    const s = scoreBasket(mask, ctx);
    if (s > bestScore) { bestScore = s; bestMask = mask; }
  }
  return [bestMask, bestScore] as const;
}

// Cache per-context optimum (small K — memoize at module load).
export const OPTIMAL: ReadonlyArray<readonly [number, number]> = (() => {
  const out: Array<readonly [number, number]> = [];
  for (let ctx = 0; ctx < N_CTX; ctx++) out.push(bestPossibleBasket(ctx));
  return out;
})();

/** Normalized score: achieved / optimal, clipped to [0, 1]. The headline
 *  H1/H2 metric. An agent that collects nothing scores 0; matching the
 *  optimal basket scores 1; random's indiscriminate grabbing is exposed
 *  by the wrong-context flip penalties. */
export function subsetAccuracy(basketMask: number, ctx: number): number {
  const [, optimalScore] = OPTIMAL[ctx];
  if (optimalScore <= 0) return 0;
  const achieved = scoreBasket(basketMask, ctx);
  return Math.max(0, achieved / optimalScore);
}

/** Hamming loss: fraction of item-bits wrong vs. optimal (lower = better). */
export function hammingLoss(basketMask: number, ctx: number): number {
  const [optimalMask] = OPTIMAL[ctx];
  let wrong = 0;
  for (let i = 0; i < K; i++) {
    if (((basketMask ^ optimalMask) >> i) & 1) wrong++;
  }
  return wrong / K;
}

/** Count forbidden pairs present in the basket (lower = better). */
export function forbiddenPairCount(basketMask: number): number {
  const present: number[] = [];
  for (let i = 0; i < K; i++) if (basketMask & (1 << i)) present.push(i);
  const s = new Set(present);
  let n = 0;
  for (const [i, j] of FORBIDDEN_PAIRS) if (s.has(i) && s.has(j)) n++;
  return n;
}

// ── Per-decision metrics (first-time F1 + duplicate rate) ────────────────
// The decision log entries both the harness and the live tick emit. isDup
// marks an item-type-already-in-basket encounter (the constraint case).
export interface DecisionLogEntry {
  item: number;
  ctx: number;
  a: number;
  isDup: boolean;
}

/** Per-decision pickup accuracy: of the decisions where the optimal action
 *  is unambiguous, what fraction did the agent get right? NOTE: dilutes
 *  against duplicates (where SKIP is now always correct by construction).
 *  Report firstTimeF1FromLog alongside it for the honest decision-quality
 *  number. */
export function pickupAccuracyFromLog(log: DecisionLogEntry[]): number {
  if (log.length === 0) return 0;
  let correct = 0;
  for (const e of log) {
    const optimal = e.isDup ? SKIP : (ITEM_VALUE[e.item][e.ctx] > 0 ? PICKUP : SKIP);
    if (e.a === optimal) correct++;
  }
  return correct / log.length;
}

/** F1 on FIRST-TIME encounters only (duplicates excluded), positive class =
 *  'should PICKUP'. This is the honest per-decision metric: duplicates are
 *  excluded (they'd pad the score post-constraint), and F1 (not accuracy)
 *  means an arm that picks up everything (the linear foil) doesn't score
 *  well on recall alone — it pays in precision for the wrong-context flip
 *  items it picks up. Returns { precision, recall, f1, support }. */
export function firstTimeF1FromLog(log: DecisionLogEntry[]): {
  precision: number; recall: number; f1: number; support: number;
} {
  let tp = 0, fp = 0, tn = 0, fn = 0;
  for (const e of log) {
    if (e.isDup) continue;
    const optimal = ITEM_VALUE[e.item][e.ctx] > 0 ? PICKUP : SKIP;
    if (optimal === PICKUP) {
      if (e.a === PICKUP) tp++; else fn++;
    } else {
      if (e.a === SKIP) tn++; else fp++;
    }
  }
  const precision = tp / Math.max(1, tp + fp);
  const recall = tp / Math.max(1, tp + fn);
  const f1 = 2 * precision * recall / Math.max(1e-9, precision + recall);
  return { precision, recall, f1, support: tp + fp + tn + fn };
}

/** Fraction of duplicate encounters where the agent PICKED UP. Must be 0
 *  after the legal-mask fix — a regression check on the constraint. */
export function duplicateRateFromLog(log: DecisionLogEntry[]): {
  rate: number; n: number;
} {
  let nDup = 0, wrong = 0;
  for (const e of log) {
    if (!e.isDup) continue;
    nDup++;
    if (e.a === PICKUP) wrong++;
  }
  return { rate: nDup === 0 ? 0 : wrong / nDup, n: nDup };
}
